/**
 * Local merchant icon resolution for Buy Source cards.
 *
 * Storefront Hunter must never call an external favicon service (Google
 * gstatic / DuckDuckGo / Clearbit) during normal rendering: it produced a
 * request storm (one call per source card per render) with no benefit over a
 * local badge.
 *
 * Resolution order:
 *   1. known local merchant badge (bundled mapping below)
 *   2. two-letter initials badge derived from the domain
 *
 * Everything renders as text inside a styled element, so there is no image
 * request at all. No remote URL is ever produced.
 */

/** Domains we recognize and render with a branded local badge. */
export const KNOWN_MERCHANTS = Object.freeze({
  "walmart.com": { text: "W", color: "#0071dc" },
  "target.com": { text: "T", color: "#cc0000" },
  "walgreens.com": { text: "WG", color: "#e31837" },
  "cvs.com": { text: "CVS", color: "#cc0000" },
  "ebay.com": { text: "eB", color: "#e53238" },
  "amazon.com": { text: "a", color: "#ff9900" },
  "homedepot.com": { text: "HD", color: "#f96302" },
  "lowes.com": { text: "L", color: "#004990" },
  "bestbuy.com": { text: "BB", color: "#0046be" },
  "kohls.com": { text: "K", color: "#002c5f" },
  "macys.com": { text: "M", color: "#e21a2c" },
  "costco.com": { text: "C", color: "#e31837" },
  "samsclub.com": { text: "SC", color: "#0067a0" },
  "staples.com": { text: "S", color: "#cc0000" },
  "officedepot.com": { text: "OD", color: "#e0192c" },
  "autozone.com": { text: "AZ", color: "#ff6600" },
  "petco.com": { text: "PC", color: "#0067b1" },
  "petsmart.com": { text: "PS", color: "#00607c" },
  "kroger.com": { text: "K", color: "#0e6ab7" },
  "publix.com": { text: "P", color: "#006c35" },
  "riteaid.com": { text: "RA", color: "#0055a5" },
  "dollartree.com": { text: "DT", color: "#00843d" },
  "newegg.com": { text: "NE", color: "#f7981d" },
  "etsy.com": { text: "E", color: "#f1641e" },
  "wayfair.com": { text: "WF", color: "#7f187f" },
  "ikea.com": { text: "IK", color: "#0058a3" },
  "bedbathandbeyond.com": { text: "BB", color: "#003f72" },
  "nordstrom.com": { text: "N", color: "#1a1a1a" },
  "sephora.com": { text: "S", color: "#d9006e" },
  "uline.com": { text: "UL", color: "#003da5" },
  "grainger.com": { text: "G", color: "#e21f26" },
  "menards.com": { text: "M", color: "#003e7e" },
  "meijer.com": { text: "MJ", color: "#e0002a" },
  "albertsons.com": { text: "AB", color: "#0058a3" },
  "wegmans.com": { text: "W", color: "#d40026" },
  "safeway.com": { text: "S", color: "#e31837" },
  "shoprite.com": { text: "SR", color: "#d1193e" },
  "traderjoes.com": { text: "TJ", color: "#b01f24" },
  "aldi.us": { text: "A", color: "#00005a" },
  "lidl.com": { text: "L", color: "#0050aa" },
  "acehardware.com": { text: "AH", color: "#d0021b" },
  "tractorsupply.com": { text: "TS", color: "#cc0000" },
  "northerntool.com": { text: "NT", color: "#00539b" },
  "zoro.com": { text: "Z", color: "#f57f20" },
  "webstaurantstore.com": { text: "WS", color: "#c8102e" },
});

/** Hosts whose only purpose is serving favicons — never fetched. */
const FAVICON_SERVICE_HOSTS = [
  "gstatic.com",
  "google.com",
  "googleusercontent.com",
  "googleapis.com",
  "duckduckgo.com",
  "clearbit.com",
  "favicon.io",
  "faviconkit.com",
  "iconhorse",
  "statvoo.com",
  "gravykitty.com",
];

export function normalizeMerchantDomain(domain) {
  return String(domain || "")
    .trim()
    .replace(/^www\./i, "")
    .toLowerCase();
}

export function isFaviconServiceUrl(url) {
  const text = String(url || "").trim();
  if (!text) return false;
  let host = "";
  try {
    // A WHATWG URL keeps a trailing-dot FQDN ("t1.gstatic.com."), which would
    // otherwise slip past the suffix check and be treated as a merchant asset.
    host = new URL(text.startsWith("//") ? `https:${text}` : text)
      .hostname.replace(/^www\./, "")
      .replace(/\.$/, "")
      .toLowerCase();
  } catch {
    return false;
  }
  if (!host) return false;
  // A favicon *service* is identified by its host, not by the word "favicon" in
  // a path — a merchant's own CDN asset (…/hyvee-favicon.png) is legitimate.
  if (FAVICON_SERVICE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) return true;
  return /^\/s2\/favicons/i.test(new URL(text.startsWith("//") ? `https:${text}` : text).pathname);
}

function initialsFor(domain, sourceName = "") {
  const base = normalizeMerchantDomain(domain) || String(sourceName || "").trim();
  if (!base) return "•";
  const label = base.includes(".") ? base.split(".")[0] : base;
  const cleaned = label.replace(/[^a-z0-9]/gi, "");
  if (!cleaned) return "•";
  // Prefer two letters for word-ish names; single letter for very short ones.
  if (cleaned.length <= 2) return cleaned.toUpperCase();
  return cleaned.slice(0, 2).toUpperCase();
}

/**
 * Local badge for a merchant. Never returns a URL.
 * @returns {{ kind: 'known'|'initials', text: string, color: string|null }}
 */
/**
 * Same-origin Ezisell merchant-icon URL. The browser must never call a
 * third-party favicon aggregation service directly; the Ezisell backend
 * resolves/caches the real icon. Returns null for un-normalizable domains.
 */
export function merchantIconUrlFor(domain) {
  const host = normalizeMerchantDomain(domain);
  if (!host) return null;
  return `/api/v1/buy-sources/merchant-icon?domain=${encodeURIComponent(host)}`;
}

export function merchantIconFor(domain, sourceName = "") {
  const host = normalizeMerchantDomain(domain);
  if (host) {
    const known = KNOWN_MERCHANTS[host];
    if (known) return { kind: "known", text: known.text, color: known.color };
    // Match a registered parent domain (e.g. "shop.target.com" -> "target.com").
    const parts = host.split(".");
    for (let i = 1; i < parts.length - 1; i += 1) {
      const parent = parts.slice(i).join(".");
      const parentKnown = KNOWN_MERCHANTS[parent];
      if (parentKnown) return { kind: "known", text: parentKnown.text, color: parentKnown.color };
    }
  }
  return { kind: "initials", text: initialsFor(host, sourceName), color: null };
}
